import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, BookOpen, Users, CreditCard, MessageSquare, GraduationCap, Mail, Shield, Scale, Home } from 'lucide-react';

const Sitemap = ({ language = 'de' }) => {
  const sections = [
    {
      title: language === 'de' ? 'Hauptseiten' : 'Main Pages',
      icon: Home,
      links: [
        { to: '/', label: language === 'de' ? 'Startseite' : 'Home' },
        { to: '/about', label: language === 'de' ? 'Über uns' : 'About Us' },
        { to: '/features', label: language === 'de' ? 'Features & Technologie' : 'Features & Technology' },
        { to: '/pricing', label: language === 'de' ? 'Preise & Pläne' : 'Pricing & Plans' },
      ]
    },
    {
      title: language === 'de' ? 'Lerninhalte' : 'Learning Content',
      icon: GraduationCap,
      links: [
        { to: '/redewendungen', label: language === 'de' ? 'Redewendungen' : 'Phrases & Idioms' },
        { to: '/cefr-levels', label: language === 'de' ? 'Sprachniveaus (A1-C2)' : 'Language Levels (A1-C2)' },
        { to: '/grammar', label: language === 'de' ? 'Grammatik-Übersicht' : 'Grammar Overview' },
      ]
    },
    {
      title: language === 'de' ? 'Grammatik' : 'Grammar',
      icon: BookOpen,
      links: [
        { to: '/grammar/articles-nouns', label: language === 'de' ? 'Artikel & Nomen' : 'Articles & Nouns' },
        { to: '/grammar/verb-basics', label: language === 'de' ? 'Verb-Grundlagen' : 'Verb Basics' },
        { to: '/grammar/questions-negations', label: language === 'de' ? 'Fragen & Verneinungen' : 'Questions & Negations' },
        { to: '/grammar/numbers-time', label: language === 'de' ? 'Zahlen & Zeit' : 'Numbers & Time' },
        { to: '/grammar/past-tense', label: language === 'de' ? 'Vergangenheit' : 'Past Tense' },
        { to: '/grammar/future-tense', label: language === 'de' ? 'Zukunft' : 'Future Tense' },
      ]
    },
    {
      title: language === 'de' ? 'Ressourcen' : 'Resources',
      icon: FileText,
      links: [
        { to: '/blog', label: language === 'de' ? 'Blog & Artikel' : 'Blog & Articles' },
        { href: '/downloads/Anfaenger-Guide-DE.pdf', label: language === 'de' ? 'E-Book (Deutsch)' : 'E-Book (German)', external: true },
        { href: '/downloads/Beginners-Guide-EN.pdf', label: language === 'de' ? 'E-Book (Englisch)' : 'E-Book (English)', external: true },
      ]
    },
    {
      title: language === 'de' ? 'Konto & Zahlung' : 'Account & Payment',
      icon: CreditCard,
      links: [
        { to: '/customer-portal', label: language === 'de' ? 'Kundenportal' : 'Customer Portal' },
        { to: '/payment-success', label: language === 'de' ? 'Zahlungsbestätigung' : 'Payment Success' },
      ]
    },
    {
      title: language === 'de' ? 'Rechtliches' : 'Legal',
      icon: Scale,
      links: [
        { to: '/impressum', label: language === 'de' ? 'Impressum' : 'Imprint' },
        { to: '/datenschutz', label: language === 'de' ? 'Datenschutzerklärung' : 'Privacy Policy' },
        { to: '/agb', label: language === 'de' ? 'AGB & Widerrufsrecht' : 'Terms & Conditions' },
        { to: '/cookies', label: language === 'de' ? 'Cookie-Richtlinien' : 'Cookie Policy' },
        { to: '/kontakt', label: language === 'de' ? 'Kontakt' : 'Contact' },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-4" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="sitemap-title">
            {language === 'de' ? 'Sitemap' : 'Sitemap'}
          </h1>
          <p className="text-gray-600">
            {language === 'de' 
              ? 'Übersicht aller Seiten auf INTERLINGUA.APP' 
              : 'Overview of all pages on INTERLINGUA.APP'}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8" data-testid="sitemap-content">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sections.map((section, idx) => (
              <div key={idx} className="space-y-3">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 border-b border-emerald-200 pb-2">
                  <section.icon className="w-5 h-5 text-emerald-600" />
                  {section.title}
                </h2>
                <ul className="space-y-2">
                  {section.links.map((link, linkIdx) => (
                    <li key={linkIdx}>
                      {link.external ? (
                        <a 
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-emerald-600 transition-colors flex items-center gap-1"
                          data-testid={`sitemap-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <FileText className="w-4 h-4" />
                          {link.label}
                        </a>
                      ) : (
                        <Link 
                          to={link.to}
                          className="text-gray-600 hover:text-emerald-600 transition-colors"
                          data-testid={`sitemap-link-${link.to.replace(/\//g, '-').substring(1) || 'home'}`}
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Last Updated */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>{language === 'de' ? 'Letzte Aktualisierung: Januar 2026' : 'Last Updated: January 2026'}</p>
        </div>
      </div>
    </div>
  );
};

export default Sitemap;
